import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { analyticsService, departmentService } from '@/services/api'
import {
  ArrowLeft, Users, CheckCircle, XCircle, Clock, Download,
  Search, Filter, TrendingUp, Briefcase,
} from 'lucide-react'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { format, parseISO, startOfMonth } from 'date-fns'
import PeriodFilter from '@/components/analytics/PeriodFilter'

const STATUS_BADGE = {
  on_time: 'badge-green', late: 'badge-yellow',
  very_late: 'badge-red', absent: 'badge-red', no_schedule: 'badge-gray',
}
const STATUS_LABEL = {
  on_time: 'Вовремя', late: 'Опоздал', very_late: 'Сильн. опозд.',
  absent: 'Не явился', no_schedule: 'Нет расп.',
}

function KPICard({ label, value, sub, color = 'text-gray-900', icon: Icon }) {
  return (
    <div className="card py-5">
      <div className="flex items-start gap-3">
        {Icon && <Icon size={20} className={`${color} shrink-0 mt-0.5`} />}
        <div>
          <p className={`text-3xl font-bold ${color}`}>{value ?? 0}</p>
          {sub && <p className="text-xs text-gray-400">{sub}</p>}
          <p className="text-sm text-gray-500 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  )
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div className="flex border-b border-gray-200 gap-1">
      {tabs.map((t) => (
        <button key={t.key} onClick={() => onChange(t.key)}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            active === t.key
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          {t.label}
        </button>
      ))}
    </div>
  )
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

function EmpRow({ e, navigate }) {
  return (
    <tr className="cursor-pointer hover:bg-gray-50"
      onClick={() => navigate(`/employees/${e.employee_id}`)}>
      <td>
        {e.photo
          ? <img src={e.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
          : <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-400">
              {e.full_name?.[0]}
            </div>}
      </td>
      <td className="font-medium text-primary-600">{e.full_name}</td>
      <td className="text-gray-500 text-sm">{e.position_name}</td>
      <td className="font-mono text-xs text-green-700">{e.check_in || '—'}</td>
      <td>
        <span className={`badge text-xs ${STATUS_BADGE[e.today_status] || 'badge-gray'}`}>
          {STATUS_LABEL[e.today_status] || e.today_status}
        </span>
      </td>
      <td>
        <span className={`badge text-xs ${e.attendance_percent >= 75 ? 'badge-green' : e.attendance_percent >= 50 ? 'badge-yellow' : 'badge-red'}`}>
          {e.attendance_percent}%
        </span>
      </td>
      <td className="text-yellow-600">{e.late_count || 0}</td>
      <td>
        <button className="btn-secondary btn-sm text-xs"
          onClick={(ev) => { ev.stopPropagation(); navigate(`/employees/${e.employee_id}`) }}>
          Профиль
        </button>
      </td>
    </tr>
  )
}

const TABS = [
  { key: 'overview', label: 'Обзор' },
  { key: 'employees', label: 'Сотрудники' },
  { key: 'stats', label: 'Статистика' },
]

export default function EmployeeDeptAnalyticsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const today = new Date()
  const [tab, setTab] = useState('overview')
  const [period, setPeriod] = useState({
    date_from: format(startOfMonth(today), 'yyyy-MM-dd'),
    date_to: format(today, 'yyyy-MM-dd'),
  })
  const [search, setSearch] = useState('')
  const [filterPosition, setFilterPosition] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['emp-dept-analytics', id, period],
    queryFn: () => analyticsService.employeeDeptDetail(id, period).then((r) => r.data),
  })

  // Positions for dropdown
  const { data: positions = [] } = useQuery({
    queryKey: ['dept-positions', id],
    queryFn: () => departmentService.positions({ department: id }).then((r) => r.data),
  })

  const empParams = useMemo(() => ({
    ...period,
    ...(search && { search }),
    ...(filterPosition && { position_id: filterPosition }),
    ...(filterStatus && { status: filterStatus }),
  }), [period, search, filterPosition, filterStatus])

  const { data: employees = [], isFetching: empsLoading } = useQuery({
    queryKey: ['emp-dept-employees', id, empParams],
    queryFn: () => analyticsService.employeeDeptEmployees(id, empParams).then((r) => r.data),
    enabled: tab === 'employees',
  })

  const handleExport = async () => {
    try {
      const res = await analyticsService.exportEmpDeptExcel(id, period)
      downloadBlob(res.data, `employees_dept_${id}_${period.date_from}_${period.date_to}.xlsx`)
    } catch { /* silent */ }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
      </div>
    )
  }
  if (!data) return <div className="text-center py-20 text-gray-400">Данные не найдены</div>

  const chartData = (data.trend || []).map((d) => ({
    label: format(parseISO(String(d.date)), 'dd.MM'),
    total: d.total, on_time: d.on_time, late: d.late,
  }))

  // Discipline = 100 - late% - absent%
  const totalEmp = data.total_employees || 0
  const latePercent = totalEmp ? Math.round((data.late_today / totalEmp) * 100) : 0
  const absentPercent = totalEmp ? Math.round((data.absent_today / totalEmp) * 100) : 0
  const disciplinePercent = Math.max(0, 100 - latePercent - absentPercent)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="btn-secondary btn-sm">
            <ArrowLeft size={16} /> Назад
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{data.department?.name}</h1>
            <p className="text-sm text-gray-500">Профиль отдела · аналитика сотрудников</p>
          </div>
        </div>
        <button onClick={handleExport} className="btn-secondary btn-sm gap-1.5">
          <Download size={15} /> Экспорт Excel
        </button>
      </div>

      <div className="card py-3">
        <PeriodFilter onChange={setPeriod} />
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <KPICard label="Всего сотрудников" value={data.total_employees} icon={Users} color="text-primary-600" />
            <KPICard label="Присутствуют сегодня" value={data.present_today} icon={CheckCircle}
              color="text-green-600" sub={`вовремя: ${data.on_time_today}, опоздало: ${data.late_today}`} />
            <KPICard label="Отсутствуют сегодня" value={data.absent_today} icon={XCircle} color="text-red-600" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <KPICard label="% вовремя за период" value={`${data.on_time_percent ?? 0}%`}
              icon={Clock} color="text-blue-600" />
            <KPICard label="Отработано, ч" value={data.total_worked_hours} icon={TrendingUp} color="text-purple-600" />
            <KPICard label="Переработка, ч" value={data.total_overtime_hours} icon={Briefcase} color="text-emerald-600" />
          </div>

          {/* Discipline KPI */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Дисциплина отдела сегодня</p>
                <p className="text-4xl font-bold text-primary-600 mt-1">{disciplinePercent}%</p>
              </div>
              <div className="text-sm text-gray-500 space-y-1 text-right">
                <p><span className="text-yellow-600 font-medium">{latePercent}%</span> опозданий</p>
                <p><span className="text-red-500 font-medium">{absentPercent}%</span> отсутствий</p>
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden flex">
              <div className="bg-green-500 h-full" style={{ width: `${100 - latePercent - absentPercent}%` }} />
              <div className="bg-yellow-400 h-full" style={{ width: `${latePercent}%` }} />
              <div className="bg-red-400 h-full" style={{ width: `${absentPercent}%` }} />
            </div>
            <div className="mt-2 flex gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />Вовремя</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" />Опоздания</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" />Отсутствия</span>
            </div>
          </div>

          {/* Today's employee status */}
          {data.employees_today?.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Статус сотрудников сегодня</h3>
              <div className="table-container max-h-80 overflow-y-auto">
                <table>
                  <thead>
                    <tr><th className="w-10"></th><th>ФИО</th><th>Должность</th><th>Приход</th><th>Статус</th><th>Опоздание</th></tr>
                  </thead>
                  <tbody>
                    {data.employees_today.map((e) => (
                      <tr key={e.employee_id} className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/employees/${e.employee_id}`)}>
                        <td>
                          {e.photo
                            ? <img src={e.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                            : <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-400 font-medium">
                                {e.full_name?.[0]}
                              </div>}
                        </td>
                        <td className="font-medium text-primary-600">{e.full_name}</td>
                        <td className="text-gray-500 text-sm">{e.position_name}</td>
                        <td className="font-mono text-xs text-green-700">{e.check_in || '—'}</td>
                        <td>
                          <span className={`badge text-xs ${STATUS_BADGE[e.status] || 'badge-gray'}`}>
                            {STATUS_LABEL[e.status] || e.status}
                          </span>
                        </td>
                        <td className="text-gray-500 text-sm">
                          {e.late_minutes > 0 ? `${e.late_minutes} мин.` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {chartData.length > 1 && (
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-4">Тренд посещаемости</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip /><Legend />
                  <Area type="monotone" dataKey="total" name="Всего" stroke="#94a3b8" fill="#f1f5f9" strokeWidth={1} />
                  <Area type="monotone" dataKey="on_time" name="Вовремя" stroke="#22c55e" fill="#dcfce7" strokeWidth={2} />
                  <Area type="monotone" dataKey="late" name="Опоздало" stroke="#f59e0b" fill="#fef3c7" strokeWidth={1} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── EMPLOYEES ── */}
      {tab === 'employees' && (
        <div className="space-y-4">
          <div className="card py-3">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-48">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input className="input pl-9 py-1.5 text-sm w-full" placeholder="Поиск по ФИО..."
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <select className="input py-1.5 text-sm" value={filterPosition}
                onChange={(e) => setFilterPosition(e.target.value)}>
                <option value="">Все должности</option>
                {(Array.isArray(positions) ? positions : positions.results || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <select className="input py-1.5 text-sm" value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">Любой статус</option>
                <option value="on_time">Вовремя</option>
                <option value="late">Опоздал</option>
                <option value="very_late">Сильн. опозд.</option>
                <option value="absent">Отсутствует</option>
              </select>
              {(search || filterPosition || filterStatus) && (
                <button className="btn-secondary btn-sm"
                  onClick={() => { setSearch(''); setFilterPosition(''); setFilterStatus('') }}>
                  <Filter size={14} /> Сбросить
                </button>
              )}
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-3">
              Сотрудники отдела
              <span className="text-gray-400 font-normal ml-2">({employees.length})</span>
            </h3>
            {empsLoading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full" />
              </div>
            ) : employees.length === 0 ? (
              <div className="text-center py-10 text-gray-400">Нет сотрудников по заданным фильтрам</div>
            ) : (
              <div className="table-container max-h-[520px] overflow-y-auto">
                <table>
                  <thead>
                    <tr>
                      <th className="w-10"></th><th>ФИО</th><th>Должность</th><th>Приход</th>
                      <th>Статус</th><th>% посещ.</th><th>Опозданий</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((e) => <EmpRow key={e.employee_id} e={e} navigate={navigate} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STATISTICS ── */}
      {tab === 'stats' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KPICard label="Всего сотрудников" value={data.total_employees} icon={Users} color="text-primary-600" />
            <KPICard label="% вовремя (период)" value={`${data.on_time_percent ?? 0}%`}
              icon={CheckCircle} color="text-green-600" />
            <KPICard label="Дисциплина отдела" value={`${disciplinePercent}%`}
              icon={TrendingUp} color="text-blue-600" />
            <KPICard label="Переработок, ч" value={data.total_overtime_hours}
              icon={Clock} color="text-emerald-600" />
          </div>

          {/* Worked vs overtime bar */}
          {chartData.length > 1 && (
            <>
              <div className="card">
                <h3 className="font-semibold text-gray-800 mb-4">Динамика посещаемости</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip /><Legend />
                    <Bar dataKey="on_time" name="Вовремя" fill="#22c55e" stackId="a" />
                    <Bar dataKey="late" name="Опоздания" fill="#f59e0b" stackId="a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="card">
                <h3 className="font-semibold text-gray-800 mb-4">Тренд (период)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip /><Legend />
                    <Area type="monotone" dataKey="on_time" name="Вовремя" stroke="#22c55e" fill="#dcfce7" strokeWidth={2} />
                    <Area type="monotone" dataKey="late" name="Опоздало" stroke="#f59e0b" fill="#fef3c7" strokeWidth={1} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {/* Summary boxes */}
          <div className="grid grid-cols-2 gap-4">
            <div className="card text-center">
              <p className="text-sm text-gray-500 mb-1">Всего отработано (период)</p>
              <p className="text-3xl font-bold text-purple-600">{data.total_worked_hours} ч</p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500 mb-1">Переработки (период)</p>
              <p className="text-3xl font-bold text-emerald-600">{data.total_overtime_hours} ч</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
